import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Personnel } from '../api/client';
import { useLang } from '../i18n/lang';
import { G } from '../i18n/glossary';

const WAGE_TYPES = [
  { v: '', label: '— select —' },
  { v: 'open', label: 'Open shop' },
  { v: 'ca_prevailing', label: 'CA prevailing wage (DIR)' },
  { v: 'davis_bacon', label: 'Federal Davis-Bacon' },
  { v: 'nv_prevailing', label: 'NV prevailing wage' },
];

export function NewProject() {
  const { t } = useLang();
  const nav = useNavigate();
  const [f, setF] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState<Personnel[]>([]);

  async function loadPeople() {
    try { setPeople((await api.getPersonnel()).personnel); } catch { /* dropdowns simply stay empty */ }
  }
  useEffect(() => { void loadPeople(); }, []);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));
  const setVal = (k: string, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!f.name?.trim()) { setError(t({ en: 'Project name is required.', fil: 'Kailangan ang project name.' })); return; }
    setBusy(true);
    try {
      const created = await api.createProject(f);
      nav(`/projects/${created.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : t({ en: 'Failed to create project', fil: 'Hindi nagawa ang project' }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <h1>{t({ en: 'New Bid', fil: 'Bagong Bid' })}</h1>
      <p className="muted">{t({ en: "Create the project workspace. After this, you'll upload documents — the AI reads them, you count.", fil: 'Gumawa ng project workspace. Pagkatapos nito, mag-a-upload ka ng mga dokumento — binabasa ng AI, ikaw ang magbibilang.' })}</p>
      <form className="card" onSubmit={submit}>
        <div className="field">
          <label htmlFor="name">{t({ en: 'Project name *', fil: 'Project name *' })}</label>
          <input id="name" value={f.name ?? ''} onChange={set('name')} required />
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="field"><label>{t({ en: 'Customer / Owner', fil: 'Customer / Owner' })}</label><input value={f.customer_name ?? ''} onChange={set('customer_name')} /></div>
          <div className="field"><label>{t({ en: 'General Contractor', fil: 'General Contractor' })}</label><input value={f.gc_name ?? ''} onChange={set('gc_name')} /></div>
          <div className="field"><label>{t({ en: 'Job address', fil: 'Job address' })}</label><input value={f.job_address ?? ''} onChange={set('job_address')} /></div>
          <div className="field"><label>{t({ en: 'GC contact', fil: 'GC contact' })}</label><input value={f.gc_contact ?? ''} onChange={set('gc_contact')} /></div>
          <div className="field"><label>{t({ en: 'County', fil: 'County' })}</label><input value={f.county ?? ''} onChange={set('county')} /></div>
          <div className="field"><label>{t({ en: 'State', fil: 'State' })}</label><input value={f.state ?? ''} onChange={set('state')} /></div>
          <div className="field"><label>{t({ en: 'Bid date', fil: 'Bid date' })}</label><input type="date" value={f.bid_date ?? ''} onChange={set('bid_date')} /></div>
          <div className="field">
            <label>{t({ en: 'Wage type', fil: 'Uri ng sahod' })}</label>
            <select value={f.wage_type ?? ''} onChange={set('wage_type')}>
              {WAGE_TYPES.map((w) => <option key={w.v} value={w.v}>{w.label}</option>)}
            </select>
          </div>
          <PersonnelField label="Salesperson" role="salesperson" value={f.salesperson ?? ''}
            people={people} onChange={(v) => setVal('salesperson', v)} onAdded={loadPeople} setError={setError} />
          <PersonnelField label="Estimator" role="estimator" value={f.estimator ?? ''}
            people={people} onChange={(v) => setVal('estimator', v)} onAdded={loadPeople} setError={setError} />
        </div>
        {error && <div className="error">{error}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? t({ en: 'Creating…', fil: 'Ginagawa…' }) : t({ en: 'Create bid', fil: 'Gumawa ng bid' })}</button>
          <button className="btn btn-ghost" type="button" onClick={() => nav('/')}>{t(G.cancel)}</button>
        </div>
      </form>
    </div>
  );
}

// Admin-managed dropdown (§5.3) for salesperson / estimator, with an inline add.
function PersonnelField({ label, role, value, people, onChange, onAdded, setError }: {
  label: string; role: string; value: string; people: Personnel[];
  onChange: (v: string) => void; onAdded: () => Promise<void> | void; setError: (s: string) => void;
}) {
  const { t } = useLang();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const options = people.filter((p) => p.role === role);

  async function add() {
    const n = name.trim();
    if (!n) return;
    setBusy(true); setError('');
    try {
      await api.addPersonnel({ name: n, role });
      await onAdded();
      onChange(n);           // select the newly added person
      setName(''); setAdding(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : t({ en: 'Could not add (admin only).', fil: 'Hindi naidagdag (admin lang).' }));
    } finally { setBusy(false); }
  }

  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 6 }}>
        <select value={value} onChange={(e) => onChange(e.target.value)} style={{ flex: 1 }}>
          <option value="">— select —</option>
          {options.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
          {value && !options.some((p) => p.name === value) && <option value={value}>{value}</option>}
        </select>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding((a) => !a)} title={t({ en: `Add ${label.toLowerCase()}`, fil: `Magdagdag ng ${label.toLowerCase()}` })}>
          {adding ? '×' : t({ en: '+ Add', fil: '+ Magdagdag' })}
        </button>
      </div>
      {adding && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t({ en: `New ${label.toLowerCase()} name`, fil: `Bagong pangalan ng ${label.toLowerCase()}` })}
            style={{ flex: 1 }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void add(); } }} autoFocus />
          <button type="button" className="btn btn-primary btn-sm" onClick={add} disabled={busy}>{busy ? t({ en: 'Adding…', fil: 'Idinadagdag…' }) : t(G.save)}</button>
        </div>
      )}
    </div>
  );
}
